/**
 * /referrals — Referral & affiliate hub (Sprint 2 / TASK 7-2).
 *
 * Mounted inside `Layout` (which delegates the chrome to `WorkspaceShell`),
 * so the centered top-nav and utility cluster stay visible.
 *
 * Behaviour:
 *   • On mount we call `ensure_referral_code` (RPC, SECURITY DEFINER) so a
 *     stable code is materialised on FIRST visit and never rotated again.
 *   • Share buttons: copy URL, WhatsApp, X / Twitter.
 *   • Earnings table (DataTable from TASK 1-5) reads `referral_events`.
 *     RLS makes sure the user only sees events tied to their own code.
 *
 * Hard rule (TASK 7-2): we never display nor allow editing of the `paid_out`
 * flag — that is admin-only and flipped manually in PHASE 9.
 */

import { Copy, Check, Gift, RefreshCw } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { useAuthentication } from '../hooks/useAuthentication';
import { supabase } from '../lib/supabase';
import Layout, { type DashboardPage } from '../modules/dashboard/components/Layout';
import { useUserProfile } from '../modules/dashboard/hooks/useUserProfile';

// ─────────────────────────────────────────────────────────────────────────
// Types — match the Postgres tables 1:1.
// ─────────────────────────────────────────────────────────────────────────

interface ReferralCodeRow {
  id:             string;
  code:           string;
  owner_user_id:  string;
  commission_pct: number;
  is_affiliate:   boolean;
  max_uses:       number | null;
  uses_count:     number;
  expires_at:     string | null;
  created_at:     string;
}

interface ReferralEventRow extends Record<string, unknown> {
  id:                 string;
  code_id:            string;
  referred_user_id:   string;
  event_type:         'signup' | 'first_payment' | 'renewal';
  payment_amount:     number | null;
  commission_amount:  number | null;
  paid_out:           boolean;
  paid_out_at:        string | null;
  created_at:         string;
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers (URL + share text)
// ─────────────────────────────────────────────────────────────────────────

const REF_QUERY_KEY = 'ref';
const STORAGE_KEY   = 'referralCode';

function buildShareUrl(code: string): string {
  if (typeof window === 'undefined') return '';
  const origin = window.location.origin;
  // Public marketing home is the right place to land — `/?ref=CODE` flows
  // into the existing AppStateManager bootstrap, which captures the code
  // before any auth UI renders.
  const url = new URL('/', origin);
  url.searchParams.set(REF_QUERY_KEY, code);
  return url.toString();
}

const NEXT_ACTION_TO_PATH: Record<string, string> = {
  // Top-level workspace
  'agent':                 '/agent',
  'dashboard':             '/dashboard',
  'jobs':                  '/jobs',
  'referrals':             '/referrals',

  // Account / settings — the Settings gear icon uses 'account-settings'
  'account-settings':      '/account-settings',
  'profile':               '/account-settings',

  // Dashboard sub-pages
  'history':               '/history',
  'resume-builder':        '/resume-builder',
  'prepare-for-interview': '/prepare-for-interview',
  'ai-companion':          '/ai-companion',
  'collaborate':           '/collaborate',
  'credits':               '/credits',
  'support':               '/support',
  'calendar-notes':        '/calendar-notes',
  'payment-history':       '/payment-history',
  'skill-assessment':      '/skill-assessment',
  'developer':             '/developer',
  'journey-setup':         '/dashboard',

  // Admin tools
  'blog-cms':              '/admin/blogs',
  'blog-editor':           '/admin/blogs/new',
  'control-panel':         '/admin/control-panel',
  'testimonials-admin':    '/admin/testimonials',
  'jobs-admin':            '/dashboard',

  // Deprecated — retired route still needs a safe fallback
  'single-optimize':       '/agent',
};

/** In-memory cache so /referrals does not full-reload when navigating away and back. */
interface ReferralPageCache {
  userId: string;
  codeRow: ReferralCodeRow;
  events: ReferralEventRow[];
}

let referralPageCache: ReferralPageCache | null = null;

function readReferralCache(userId: string | undefined): ReferralPageCache | null {
  if (!userId || !referralPageCache || referralPageCache.userId !== userId) return null;
  return referralPageCache;
}

function writeReferralCache(userId: string, codeRow: ReferralCodeRow, events: ReferralEventRow[]) {
  referralPageCache = { userId, codeRow, events };
}

// ─────────────────────────────────────────────────────────────────────────
// Page component
// ─────────────────────────────────────────────────────────────────────────

export interface ReferralPageProps {
  onSignOut: () => void | Promise<void>;
}

const ReferralPage: React.FC<ReferralPageProps> = ({ onSignOut }) => {
  const { t, i18n } = useTranslation();
  const { session, user } = useAuthentication();
  const { profile } = useUserProfile();
  const userId = session?.user?.id;
  const bootCache = readReferralCache(userId);

  const [codeRow, setCodeRow]       = useState<ReferralCodeRow | null>(bootCache?.codeRow ?? null);
  const [events,  setEvents]        = useState<ReferralEventRow[]>(bootCache?.events ?? []);
  const [loading, setLoading]       = useState(!bootCache);
  const [refreshing, setRefreshing] = useState(false);
  const [error,   setError]         = useState<string | null>(null);
  const [copied,  setCopied]        = useState(false);
  const fetchedForUserRef = useRef<string | null>(bootCache ? userId ?? null : null);

  // ── Resolve display name for the suffix passed to the SQL helper. ──
  const displayName = useMemo<string>(() => {
    return (
      profile?.displayName
      ?? (user?.user_metadata as Record<string, unknown> | undefined)?.full_name as string | undefined
      ?? (user?.user_metadata as Record<string, unknown> | undefined)?.name as string | undefined
      ?? (session?.user.email ? session.user.email.split('@')[0] : '')
      ?? ''
    );
  }, [profile?.displayName, session?.user.email, user?.user_metadata]);
  const displayNameRef = useRef(displayName);
  displayNameRef.current = displayName;

  // ── Bootstrap: ensure the user has a stable code; load earnings. ──
  const loadAll = useCallback(async (opts?: { showSpinner?: boolean }) => {
    if (!session?.user?.id) {
      setLoading(false);
      return;
    }

    const showSpinner = opts?.showSpinner ?? !readReferralCache(session.user.id);
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    setError(null);

    try {
      // 1. ensure_referral_code → returns the existing row (no rotation).
      const { data: ensuredRow, error: rpcErr } = await supabase.rpc(
        'ensure_referral_code',
        { p_display_name: displayNameRef.current },
      );
      if (rpcErr) throw rpcErr;

      // RPC returns the row as a JSON object (not an array) since it
      // returns a single composite type.
      const row = (Array.isArray(ensuredRow) ? ensuredRow[0] : ensuredRow) as
        | ReferralCodeRow
        | null;

      if (row) {
        setCodeRow(row);

        // 2. Earnings: events tied to this code (RLS already restricts).
        const { data: evRows, error: evErr } = await supabase
          .from('referral_events')
          .select(
            'id, code_id, referred_user_id, event_type, payment_amount, commission_amount, paid_out, paid_out_at, created_at',
          )
          .eq('code_id', row.id)
          .order('created_at', { ascending: false });
        if (evErr) throw evErr;

        const nextEvents = (evRows ?? []) as ReferralEventRow[];
        setEvents(nextEvents);
        writeReferralCache(session.user.id, row, nextEvents);
        fetchedForUserRef.current = session.user.id;
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('views.referrals.loadFailed', 'Could not load referral data.');
      setError(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [session?.user?.id, t]);

  useEffect(() => {
    if (!session?.user?.id) {
      referralPageCache = null;
      fetchedForUserRef.current = null;
      setLoading(false);
      return;
    }

    const cache = readReferralCache(session.user.id);
    if (cache) {
      setCodeRow(cache.codeRow);
      setEvents(cache.events);
      setLoading(false);
      fetchedForUserRef.current = session.user.id;
      return;
    }

    if (fetchedForUserRef.current === session.user.id) return;

    void loadAll({ showSpinner: true });
  }, [session?.user?.id, loadAll]);

  // ── Share handlers ──
  const shareUrl     = codeRow ? buildShareUrl(codeRow.code) : '';
  const shareMessage = codeRow
    ? t('views.referrals.shareMessage', {
        defaultValue: "I'm using GoGrabJob to AI-optimise my resume and land interviews. Sign up with my referral and get bonus credits: {{url}}",
        url: shareUrl,
      })
    : '';

  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback for environments without Clipboard API.
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(ta);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }
  }, [shareUrl]);

  const handleWhatsApp = useCallback(() => {
    if (!shareMessage) return;
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareMessage)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [shareMessage]);

  const handleLinkedIn = useCallback(() => {
    if (!shareUrl) return;
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [shareUrl]);

  // ── Earnings rollup (above the table) ──
  const stats = useMemo(() => {
    let signups       = 0;
    let firstPayments = 0;
    let renewals      = 0;
    let pendingCents  = 0;
    let paidCents     = 0;

    for (const ev of events) {
      if (ev.event_type === 'signup')        signups       += 1;
      if (ev.event_type === 'first_payment') firstPayments += 1;
      if (ev.event_type === 'renewal')       renewals      += 1;
      const c = Math.round(((ev.commission_amount ?? 0) as number) * 100);
      if (ev.paid_out) paidCents    += c;
      else             pendingCents += c;
    }
    return {
      signups,
      firstPayments,
      renewals,
      pendingInr: pendingCents / 100,
      paidInr:    paidCents    / 100,
    };
  }, [events]);

  const dateLocaleMap: Record<string, string> = { en: 'en-US', es: 'es-ES', pt: 'pt-BR', ru: 'ru-RU' };
  const localeForDate = dateLocaleMap[i18n.language?.split('-')[0] || ''] ?? i18n.language ?? undefined;

  // ── DataTable columns ──
  const columns = useMemo<Column<ReferralEventRow>[]>(() => [
    {
      key: 'created_at',
      header: t('views.referrals.colDate', 'Date'),
      minWidth: 140,
      cell: (v) => {
        const d = new Date(String(v));
        return d.toLocaleString(localeForDate, { dateStyle: 'medium', timeStyle: 'short' });
      },
    },
    {
      key: 'event_type',
      header: t('views.referrals.colEvent', 'Event'),
      minWidth: 130,
      cell: (v) => <EventBadge type={String(v) as ReferralEventRow['event_type']} />,
    },
    {
      key: 'payment_amount',
      header: t('views.referrals.colPayment', 'Payment (₹)'),
      minWidth: 130,
      cell: (v) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN')}`),
    },
    {
      key: 'commission_amount',
      header: t('views.referrals.colEarning', 'Your earning (₹)'),
      minWidth: 150,
      cell: (v) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN')}`),
    },
    {
      key: 'paid_out',
      header: t('views.referrals.colStatus', 'Status'),
      minWidth: 110,
      cell: (v) => (v ? <PaidBadge /> : <PendingBadge />),
    },
  ], [t, localeForDate]);

  // ── Layout-level navigation handler (mirrors AgentPage). ──
  const handleNavigate = (page: DashboardPage) => {
    const target = NEXT_ACTION_TO_PATH[page] || '/dashboard';
    window.history.pushState({}, '', target);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <Layout currentPage="referrals" onNavigate={handleNavigate} onSignOut={onSignOut}>
      <div key={i18n.language} className="ds-page ds-referral-page px-3 sm:px-4 lg:px-6 py-4 sm:py-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] max-w-[1100px] mx-auto w-full min-w-0 overflow-x-hidden">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-1">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="ds-icon-square ds-icon-square-lg shrink-0">
              <Gift className="w-5 h-5" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="t-page-title">
                {t('views.referrals.title', 'Refer & earn')}
              </h1>
              <p className="t-body mt-0.5">
                {t('views.referrals.subtitle', {
                  defaultValue: 'Share your code. Earn a commission on every paid signup — {{pct}}% of each payment.',
                  pct: codeRow?.commission_pct ?? 10,
                })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void loadAll({ showSpinner: false })}
            className="ds-btn ds-btn-secondary min-h-[44px] w-full sm:w-auto touch-manipulation shrink-0"
            aria-label={t('views.referrals.refreshAria', 'Refresh referral data')}
            disabled={loading || refreshing}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} aria-hidden />
            <span className="hidden sm:inline">{t('views.referrals.refresh', 'Refresh')}</span>
          </button>
        </div>

        {/* ── Code + share card ── */}
        <section
          data-tour="referrals-code-card"
          className="ds-card mt-4"
        >
          {error ? (
            <div className="ds-recessed ds-recessed-danger t-meta">
              {error}
            </div>
          ) : null}

          <div className="flex flex-col gap-4 mt-1">
            <div className="min-w-0 w-full">
              <div className="t-label mb-1">
                {t('views.referrals.yourCode', 'Your referral code')}
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <code
                  className="ds-recessed t-hero t-mono t-num select-all w-full text-center sm:text-left break-all"
                >
                  {codeRow?.code ?? (loading ? '••••••••' : '—')}
                </code>
              </div>
            </div>

            {/* Share URL preview */}
            <div className="min-w-0 w-full">
              <div className="t-label mb-1">
                {t('views.referrals.shareLink', 'Share link')}
              </div>
              <div
                className="ds-recessed t-meta t-mono flex items-center gap-1 overflow-hidden"
              >
                <span className="flex-1 min-w-0 truncate">{shareUrl || '—'}</span>
              </div>
            </div>
          </div>

          {/* Share buttons */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 mt-4">
            <ShareBtn
              icon={copied ? Check : Copy}
              label={copied ? t('views.referrals.copied', 'Copied!') : t('views.referrals.copyUrl', 'Copy URL')}
              onClick={handleCopy}
              disabled={!codeRow}
              variant={copied ? 'success' : 'primary'}
            />
            <ShareBtn
              icon={WhatsAppIcon}
              label={t('views.referrals.shareWhatsApp', 'Share on WhatsApp')}
              onClick={handleWhatsApp}
              disabled={!codeRow}
              variant="whatsapp"
            />
            <ShareBtn
              icon={LinkedInIcon}
              label={t('views.referrals.shareLinkedIn', 'Share on LinkedIn')}
              onClick={handleLinkedIn}
              disabled={!codeRow}
              variant="linkedin"
            />
          </div>

          <p className="t-meta mt-3">
            {t('views.referrals.codesStableNote', 'Codes are stable — once issued they never rotate. Payouts happen manually after admin verification (PHASE 9).')}
          </p>
        </section>

        {/* ── KPI tiles ── */}
        <section className="mt-5 grid grid-cols-2 lg:grid-cols-5 gap-2.5">
          <KpiTile label={t('views.referrals.kpiSignups', 'Signups')} shortLabel={t('views.referrals.kpiSignupsShort', 'Signups')} value={String(stats.signups)} />
          <KpiTile label={t('views.referrals.kpiFirstPayments', 'First payments')} shortLabel={t('views.referrals.kpiFirstPaymentsShort', 'Payments')} value={String(stats.firstPayments)} />
          <KpiTile label={t('views.referrals.kpiRenewals', 'Renewals')} shortLabel={t('views.referrals.kpiRenewalsShort', 'Renewals')} value={String(stats.renewals)} />
          <KpiTile label={t('views.referrals.kpiPending', 'Pending')} shortLabel={t('views.referrals.kpiPending', 'Pending')} value={`₹${stats.pendingInr.toLocaleString('en-IN')}`} highlight />
          <KpiTile label={t('views.referrals.kpiPaidOut', 'Paid out')} shortLabel={t('views.referrals.kpiPaidOut', 'Paid out')} value={`₹${stats.paidInr.toLocaleString('en-IN')}`} />
        </section>

        {/* ── Earnings table ── */}
        <section className="mt-5">
          <h2 className="t-h2 mb-2">
            {t('views.referrals.earningsTitle', 'Earnings')}
          </h2>
          <DataTable<ReferralEventRow>
            columns={columns}
            rows={events}
            loading={loading}
            ariaLabel={t('views.referrals.earningsAria', 'Referral earnings')}
            emptyMessage={t('views.referrals.emptyMessage', 'No referral activity yet — share your code to start earning.')}
            pageSizeOptions={[10, 20, 50]}
            initialPageSize={10}
          />
        </section>
      </div>
    </Layout>
  );
};

export default ReferralPage;

// ─────────────────────────────────────────────────────────────────────────
// Brand SVG icons
// ─────────────────────────────────────────────────────────────────────────

function WhatsAppIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#25D366" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function LinkedInIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#0A66C2" aria-hidden>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Small presentational helpers
// ─────────────────────────────────────────────────────────────────────────

interface ShareBtnProps {
  icon:        React.ComponentType<{ className?: string }>;
  label:       string;
  onClick:     () => void;
  disabled?:   boolean;
  variant?:    'default' | 'primary' | 'success' | 'whatsapp' | 'linkedin';
}

function ShareBtn({ icon: Icon, label, onClick, disabled, variant = 'default' }: ShareBtnProps) {
  const variantClass =
    variant === 'success'
      ? 'ds-btn-secondary ds-chip-ok'
      : variant === 'primary'
        ? 'ds-btn-secondary'
        : variant === 'whatsapp'
          ? 'ds-btn-ghost'
          : variant === 'linkedin'
            ? 'ds-btn-ghost'
            : 'ds-btn-ghost';

  const style: React.CSSProperties =
    variant === 'whatsapp'
      ? { borderColor: 'rgba(37,211,102,0.35)', color: '#25D366' }
      : variant === 'linkedin'
        ? { borderColor: 'rgba(10,102,194,0.35)', color: '#0A66C2' }
        : {};

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`ds-btn ds-btn-sm ${variantClass} w-full sm:w-auto justify-center min-h-[44px] touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed`}
      style={style}
      aria-label={label}
    >
      <Icon className="w-4 h-4 shrink-0" aria-hidden />
      <span>{label}</span>
    </button>
  );
}

function EventBadge({ type }: { type: ReferralEventRow['event_type'] }) {
  const { t } = useTranslation();
  const styleMap: Record<ReferralEventRow['event_type'], { className: string; labelKey: string; defaultLabel: string }> = {
    signup:        { className: 'ds-chip-brand', labelKey: 'views.referrals.eventSignup', defaultLabel: 'Signup' },
    first_payment: { className: 'ds-chip-ok', labelKey: 'views.referrals.eventFirstPayment', defaultLabel: 'First payment' },
    renewal:       { className: 'ds-chip-ok', labelKey: 'views.referrals.eventRenewal', defaultLabel: 'Renewal' },
  };
  const s = styleMap[type];
  return (
    <span className={`ds-chip ${s.className}`}>
      {t(s.labelKey, s.defaultLabel)}
    </span>
  );
}

function PaidBadge() {
  const { t } = useTranslation();
  return (
    <span className="ds-chip ds-chip-ok">
      {t('views.referrals.statusPaidOut', 'Paid out')}
    </span>
  );
}

function PendingBadge() {
  const { t } = useTranslation();
  return (
    <span className="ds-chip ds-chip-warn">
      {t('views.referrals.statusPending', 'Pending')}
    </span>
  );
}

function KpiTile({ label, shortLabel, value, highlight = false }: { label: string; shortLabel?: string; value: string; highlight?: boolean }) {
  return (
    <div className={`ds-card-sm ${highlight ? 'ds-recessed-info' : ''}`}>
      <div className="t-label">
        {shortLabel ? (
          <>
            <span className="sm:hidden">{shortLabel}</span>
            <span className="hidden sm:inline">{label}</span>
          </>
        ) : label}
      </div>
      <div className="t-h3 t-num mt-0.5">
        {value}
      </div>
    </div>
  );
}
